// G360_K470 - sort, filter, column choice and CSV export (the shared DataTable).
// G360_K462 - a failed read is said, not shown as an empty list or a zero; a refused change is said, not shown as done.
// Cross-tenant audit - every staff action across the platform. Immutable.
import { useEffect, useState } from 'react';
import { FileSearch, ShieldCheck } from 'lucide-react';
import { listAdminAudit, listTenants } from '../lib/adminApi.js';
import { DataTable } from '@govern360/shared/components/datatable.jsx';


export default function AuditAllPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState('');
  const [names, setNames] = useState({});
  /* G360_K470 - the newest 1000 events (was 150, and the page did not
     say it was a slice). Tenant names are read alongside; if they cannot
     be read the column falls back to the id prefix it showed before. */
  const LIMIT = 1000;
  useEffect(() => {
    listAdminAudit(LIMIT).then(({ data, error }) => { setLoadErr(error ? (error.message || 'the read failed') : ''); setRows(data); setLoading(false); });
    listTenants().then(({ data, error }) => {
      if (error) return;
      const m = {}; (data || []).forEach((t) => { m[t.id] = t.name; }); setNames(m);
    });
  }, []);
  const tenantLabel = (id) => (id ? (names[id] || id.slice(0, 8)) : null);
  const detailText = (r) => (r.detail && typeof r.detail === 'object' ? Object.entries(r.detail).map(([k, v]) => `${k}=${typeof v === 'object' ? JSON.stringify(v) : v}`).join(', ') : (r.detail || null));
  const COLS = [
    { key: 'created_at', label: 'Time', value: (r) => (r.created_at ? new Date(r.created_at).toLocaleString() : null),
      sort: (r) => (r.created_at ? new Date(r.created_at).getTime() : null), csv: (r) => r.created_at || '',
      render: (r) => <span className="mono" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{r.created_at ? new Date(r.created_at).toLocaleString() : '-'}</span> },
    { key: 'level', label: 'Level', render: (r) => <span className={`mono lv-${r.level}`}>{r.level}</span> },
    { key: 'actor_email', label: 'Actor', render: (r) => <span className="mono" style={{ fontSize: 11 }}>{r.actor_email}</span> },
    { key: 'event', label: 'Event', render: (r) => <span className="mono">{r.event}</span> },
    { key: 'tenant', label: 'Tenant', value: (r) => tenantLabel(r.target_tenant_id), csv: (r) => (r.target_tenant_id ? (names[r.target_tenant_id] ? names[r.target_tenant_id] + ' (' + r.target_tenant_id + ')' : r.target_tenant_id) : '') },
    { key: 'detail', label: 'Detail', value: detailText,
      render: (r) => <span style={{ color: 'var(--g-text-2)', fontSize: 12 }}>{detailText(r)}</span> },
  ];

  return (
    <div>
      <div className="ph"><div><h1 className="ph-h1">Cross-tenant audit</h1><p className="ph-sub">Every staff action across the entire platform. Immutable.</p></div></div>
      <div className="banner admin"><ShieldCheck size={18} /><span>This log records every internal staff action &mdash; views, impersonations, plan changes, deploys. Tamper-proof (append-only).</span></div>
      {loading ? (
        <div className="card" style={{ textAlign: 'center', padding: 48, color: 'var(--g-text-2)' }}>Loading&hellip;</div>
      ) : loadErr ? (
        <div className="card g360-admin-err" style={{ padding: 24, borderLeft: '3px solid var(--g-danger)' }}>
          <p style={{ color: 'var(--g-danger)', fontSize: 14, margin: '0 0 8px' }}>The audit log could not be read</p>
          <p style={{ fontSize: 13, color: 'var(--g-text-2)', margin: 0 }}>{loadErr}. This is not the same as there being none.</p>
        </div>
      ) : rows.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 56 }}>
          <FileSearch size={40} style={{ color: 'var(--g-text-3)' }} />
          <p style={{ marginTop: 14, color: 'var(--g-text-2)' }}>No staff actions logged yet.</p>
          <p style={{ fontSize: 12, color: 'var(--g-text-3)' }}>Impersonation and other staff actions will appear here.</p>
        </div>
      ) : (
        <>
          {rows.length >= LIMIT && (
            <p style={{ fontSize: 12, color: 'var(--g-text-2)', margin: '0 0 8px' }}>
              The newest {LIMIT.toLocaleString()} events. Older events are kept but not loaded here.
            </p>
          )}
          <DataTable rows={rows} columns={COLS} storageKey="g360.admin.audit.hiddenCols" name="admin-audit"
            placeholder="Filter by actor, event, tenant or detail..." rowKey={(r) => r.id} />
        </>
      )}
    </div>
  );
}
